import { FC, JSX, useEffect, useMemo, useRef, useState } from "react";
import { ContractEntityAffiliationModel } from "@app/projects/servicing-provider/providerAffiliation";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { Accordion } from "@app/shared/Accordion";
import { AffiliationType, EntityTypeEnum } from "@app/projects/enumerators";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesIcon } from "@app/shared/icons/QESIcon";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { orderBy } from "lodash";
import ProviderAffiliationService from "@app/projects/providerAffiliationsService";
import { QESButton } from "@app/shared/qes-standards/Button";

interface AffiliationTableData {
  [wafflesKeyColumnName]: string;
  affiliationType: string;
  name: string;
  nameDisplay: JSX.Element | string;
  npi?: string;
  type: string;
  affiliationStrength?: string;
  primary?: string;
  entitySize: string;
}

// Message shown in place of a hyperlink for a contract entity / organization that has no Provider Hub
// profile (no provider has it as a primary affiliation) - clicking such a name would spin forever.
export const NO_PROFILE_NOTE = "(No profile available - no providers with a primary affiliation)";

// The fallback row is fetched once, but its name rendering depends on profile availability that can resolve
// later. Hold the fetched fields only (no JSX) and derive nameDisplay from current props at render time.
type FallbackRowData = Omit<AffiliationTableData, "nameDisplay"> & { contractEntityId: number };

interface ProviderProfileAffiliationAccordionProps {
  affiliations: ContractEntityAffiliationModel[];
  loading: boolean;
  entityType: EntityTypeEnum;
  providerAffiliationVersionId: number;
  professionalAffiliationId?: number;
  professionalContractEntityName?: string;
  isGhost?: boolean;
  onContractEntityClick: (contractEntityId: number) => void;
  onOrganizationClick: (organizationNpi: string) => void;
  contractEntityIdsWithoutProfile: Set<number>;
  organizationNavContractEntityIdByNpi: Map<string, number>;
  isOrganizationAvailabilityResolved: boolean;
}

export const ProviderProfileAffiliationAccordion: FC<ProviderProfileAffiliationAccordionProps> = (props) => {
  const providerAffiliationService = useRef(new ProviderAffiliationService()).current;

  const [fallbackTableRow, setFallbackTableRow] = useState<FallbackRowData | null>(null);
  const [isLoadingFallback, setIsLoadingFallback] = useState(false);

  // Plain (non-hyperlink) name plus the "no profile" note, for entities with no profile to route to.
  const renderNoProfileName = (name: string, testId: string): JSX.Element => (
    <span data-testid={testId}>
      {name} <span className="provider-affiliation__no-profile-note">{NO_PROFILE_NOTE}</span>
    </span>
  );

  const renderContractEntityName = (contractEntityId: number, name: string): JSX.Element => {
    if (props.contractEntityIdsWithoutProfile.has(contractEntityId)) {
      return renderNoProfileName(name, `affiliation__ce-no-profile--${contractEntityId}`);
    }
    return (
      <QESButton qesType="link" qesTestId={`affiliation__ce-link--${contractEntityId}`} onClick={() => props.onContractEntityClick(contractEntityId)}>
        {name}
      </QESButton>
    );
  };

  // An organization can only be linked once the availability lookup has resolved the contract entity its
  // profile lives under - without that the click has nowhere to go. Until then (and if the lookup failed) the
  // name is plain text with no note, since we cannot yet claim the organization has no profile.
  const renderOrganizationName = (npi2: string, name: string): JSX.Element => {
    if (!props.isOrganizationAvailabilityResolved) {
      return <span data-testid={`affiliation__org-unresolved--${npi2}`}>{name}</span>;
    }
    if (!props.organizationNavContractEntityIdByNpi.has(npi2)) {
      return renderNoProfileName(name, `affiliation__org-no-profile--${npi2}`);
    }
    return (
      <QESButton qesType="link" qesTestId={`affiliation__org-link--${npi2}`} onClick={() => props.onOrganizationClick(npi2)}>
        {name}
      </QESButton>
    );
  };

  useEffect(() => {
    if (props.loading || props.affiliations.length > 0 || !props.professionalContractEntityName) {
      setIsLoadingFallback(false);
      setFallbackTableRow((prev) => prev !== null ? null : prev);
      return;
    }
    let cancelled = false;
    setIsLoadingFallback(true);
    providerAffiliationService
      .getContractEntityHeaderFieldsById(props.providerAffiliationVersionId, props.professionalAffiliationId)
      .then((ceInfo) => {
        if (!cancelled && props?.isGhost !== true) {
          setFallbackTableRow({
            affiliationType: AffiliationType.ProfessionalAffiliation,
            contractEntityId: props.professionalAffiliationId ?? 0,
            entitySize: ceInfo.entitySize,
            name: props.professionalContractEntityName,
            type: ceInfo.entityType,
            [wafflesKeyColumnName]: props.professionalContractEntityName,
          });
        }
      })
      .catch(() => { if (!cancelled) setFallbackTableRow(null); })
      .finally(() => { if (!cancelled) setIsLoadingFallback(false); });
    return () => { cancelled = true; };
  }, [props.loading, props.affiliations, props.professionalContractEntityName, props.professionalAffiliationId]);

  const entityTypeTooltip = useMemo(() => (
    <div className="entity-type-tooltip-body text-start p-2">
      <div className="header-row row border-bottom py-1">
        <div className="col-6 label-s">Type</div>
        <div className="col-6 label-s">Description</div>
      </div>
      <div className="row my-2">
        <div className="col label-tiny">Small Independent</div>
        <div className="col">5 or fewer MD/DO Providers</div>
      </div>
      <div className="row my-2">
        <div className="col label-tiny">Medium Independent</div>
        <div className="col">6-50 MD/DO Providers</div>
      </div>
      <div className="row my-2">
        <div className="col label-tiny">Large Independent</div>
        <div className="col">Over 50 MD/DO Providers</div>
      </div>
      <div className="row my-2">
        <div className="col label-tiny">Non-IDN Health System</div>
        <div className="col">Owns a single hospital (provider count can vary)</div>
      </div>
      <div className="row my-2">
        <div className="col label-tiny">IDN (Integrated Delivery Network)</div>
        <div className="col">Owns two or more hospitals (provider count can vary)</div>
      </div>
    </div>
  ), []);

  /* oxlint-disable sort-keys */
  const columnConfig: ColumnConfig = {
    nameDisplay: {
      displayText: "Name",
      testId: "provider-hub_affiliation-table-display-name",
      sortOrder: 1,
      sizeContentToFit: true,
    },
    type: {
      displayText: "Type",
      testId: "provider-hub_affiliation-table-type",
      sortOrder: 2,
      maxWidth: "225px",
      sizeContentToFit: true,
      displayElement: (
        <TooltipWrapper containerClassName="entity-type-tooltip-wrapper" text={entityTypeTooltip} position="top">
          <QesIcon className="me-2" iconName={QesIcons.InfoCircle} testId="tooltip-entity-type" description="Show tooltip" />
          Type
        </TooltipWrapper>
      ),
    },
    npi: {
      displayText: "NPI",
      testId: "provider-hub_affiliation-table-npi",
      sortOrder: 3,
      maxWidth: "150px",
      noSort: true,
      sizeContentToFit: true,
    },
    entitySize: {
      displayText: "Entity Size (MD/DO)",
      testId: "provider-hub_affiliation-table-affiliation-entity-size",
      sortOrder: 4,
      maxWidth: "175px",
      sizeContentToFit: true,
    },
    affiliationStrength: {
      displayText: "Affiliation Strength",
      testId: "provider-hub_affiliation-table-affiliationstrength",
      sortOrder: 5,
      maxWidth: "50px",
      sizeContentToFit: true,
    },
    primary: {
      displayText: "",
      testId: "provider-hub_contract-entity-table-primary",
      sortOrder: 6,
      noSort: true,
      maxWidth: "50px",
      sizeContentToFit: true,
    },
    name: {
      hidden: true,
      noSort: true,
      testId: "provider-hub_contract-entity-table-name",
    },
  };
  /* oxlint-enable sort-keys */

  const customSort = (data: AffiliationTableData[], column: keyof AffiliationTableData, direction: SortDirections) => {
    const dir = direction === SortDirections.Desc ? "desc" : "asc";
    switch (column) {
      case "affiliationStrength":
        return orderBy(data, (d) => Number((d.affiliationStrength ?? "0%").slice(0, -1)), dir);
      case "nameDisplay":
        return orderBy(data, (d) => d.name?.toLowerCase() ?? "", dir);
      case "type":
        return orderBy(data, (d) => d.type, dir);
      case "entitySize":
        return orderBy(data, (d) => d.entitySize, dir);
      default:
        return data;
    }
  };

  const allSorted = useMemo(() =>
    props.affiliations.slice().sort((a, b) => b.contractEntityAffiliationStrength - a.contractEntityAffiliationStrength),
    [props.affiliations]
  );

  const allTableData = useMemo<AffiliationTableData[]>(() =>
    allSorted.map((record, index) => ({
      affiliationStrength: `${Math.round(record.contractEntityAffiliationStrength * 100)}%`,
      affiliationType: record.contractEntityAffiliationType,
      entitySize: record.entitySize,
      name: record.contractEntityName,
      nameDisplay: renderContractEntityName(record.contractEntityId, record.contractEntityName),
      npi: "---",
      primary: record.contractEntityPrimary ? "Primary" : "",
      type: record.contractEntitySubType,
      [wafflesKeyColumnName]: index.toString(),
    })),
    [allSorted, props.onContractEntityClick, props.contractEntityIdsWithoutProfile]
  );

  const expansionData = useMemo(() => {
    const map = new Map<string, AffiliationTableData[]>();
    allSorted.forEach((record, index) => {
      const npi2s = record.npi2Affiliations ?? [];
      const rows: AffiliationTableData[] = npi2s.length > 0
        ? npi2s
          .sort((a, b) => Number(b.affiliationStrength) - Number(a.affiliationStrength))
          .map((org) => ({
            affiliationStrength: `${Math.round(Number(org.affiliationStrength) * 100)}%`,
            affiliationType: "",
            entitySize: "",
            name: org.name,
            nameDisplay: renderOrganizationName(org.npi2, org.name),
            npi: org.npi2,
            primary: org.isPrimaryOrganization ? "Primary" : "",
            type: "",
            [wafflesKeyColumnName]: org.npi2,
          }))
        : [{
          affiliationStrength: "---",
          affiliationType: "",
          entitySize: "",
          name: "---",
          nameDisplay: "---",
          npi: "---",
          primary: "---",
          type: "---",
          [wafflesKeyColumnName]: "---",
        }];
      map.set(index.toString(), rows);
    });
    return map;
  }, [allSorted, props.organizationNavContractEntityIdByNpi, props.isOrganizationAvailabilityResolved, props.onOrganizationClick]);

  const professionalData = useMemo(() => {
    if (props.affiliations.length === 0 && fallbackTableRow !== null) {
      // Keys stay in this order - Waffles takes column order from row-object key insertion order.
      return [{
        affiliationType: fallbackTableRow.affiliationType,
        entitySize: fallbackTableRow.entitySize,
        name: fallbackTableRow.name,
        nameDisplay: renderContractEntityName(fallbackTableRow.contractEntityId, fallbackTableRow.name),
        type: fallbackTableRow.type,
        [wafflesKeyColumnName]: fallbackTableRow[wafflesKeyColumnName],
      }];
    }
    return allTableData.filter((x) => x.affiliationType === AffiliationType.ProfessionalAffiliation);
  }, [props.affiliations, fallbackTableRow, allTableData, props.contractEntityIdsWithoutProfile, props.onContractEntityClick]);

  const institutionalData = useMemo(() =>
    allTableData.filter((x) => x.affiliationType === AffiliationType.InstitutionalAffiliation),
    [allTableData]
  );
  const isLoading = props.loading || isLoadingFallback;

  const loadingContent = (
    <div className="text-center py-4">
      <QesIcon iconName={QesIcons.LoadingV1} className="qes-icon-spin" description="Loading affiliations" width="32" />
      <p className="mt-2">Loading affiliations...</p>
    </div>
  );

  if (props.entityType === EntityTypeEnum.Facility) {
    return (
      <Accordion title="Contract Entity" defaultOpen={true} testId="affiliation__facility-accordion">
        {isLoading ? loadingContent : (
          <Waffles<AffiliationTableData>
            columns={columnConfig}
            data={professionalData}
            testId="provider-affiliationprofessional-results"
            fullWidth={true}
            dataExpansions={expansionData}
            customSort={customSort}
          />
        )}
      </Accordion>
    );
  }

  return (
    <>
      <Accordion title="Contract Entity (Professional Affiliation)" defaultOpen={true} testId="affiliation__professional-accordion">
        {isLoading ? loadingContent : (
          <Waffles<AffiliationTableData>
            columns={columnConfig}
            data={professionalData}
            testId="provider-affiliationprofessional-results"
            fullWidth={true}
            dataExpansions={expansionData}
            customSort={customSort}
          />
        )}
      </Accordion>
      <Accordion title="Institutional Affiliation" defaultOpen={true} testId="affiliation__institutional-accordion">
        {isLoading ? loadingContent : (
          <Waffles<AffiliationTableData>
            columns={columnConfig}
            data={institutionalData}
            testId="provider-affiliationinstitutional-results"
            fullWidth={true}
            dataExpansions={expansionData}
            customSort={customSort}
          />
        )}
      </Accordion>
    </>
  );
};